import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { useLang } from "@/context/LanguageContext";
import { ArrowLeft, Mail, MoveHorizontal, ChevronsLeftRight, MoveVertical } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LABELS = {
  it: { dimensions: "Dimensioni", width: "Larghezza", depth: "Profondità", height: "Altezza", code: "Codice", upholstery: "Rivestimento", custom: "Personalizzabile su misura in dimensioni, tessuti e finiture.", request: "Richiedi questo modello", back: "Torna al sito", notFound: "Prodotto non trovato", loading: "Caricamento…" },
  en: { dimensions: "Dimensions", width: "Width", depth: "Depth", height: "Height", code: "Code", upholstery: "Upholstery", custom: "Made to measure and customisable in size, fabrics and finishes.", request: "Request this model", back: "Back to site", notFound: "Product not found", loading: "Loading…" },
  fr: { dimensions: "Dimensions", width: "Largeur", depth: "Profondeur", height: "Hauteur", code: "Code", upholstery: "Revêtement", custom: "Fabriqué sur mesure et personnalisable en dimensions, tissus et finitions.", request: "Demander ce modèle", back: "Retour au site", notFound: "Produit introuvable", loading: "Chargement…" },
  es: { dimensions: "Dimensiones", width: "Ancho", depth: "Profundidad", height: "Altura", code: "Código", upholstery: "Tapizado", custom: "Fabricado a medida y personalizable en dimensiones, tejidos y acabados.", request: "Solicitar este modelo", back: "Volver al sitio", notFound: "Producto no encontrado", loading: "Cargando…" },
};

export default function Product() {
  const { code } = useParams();
  const { lang } = useLang();
  const L = LABELS[lang] || LABELS.it;
  const [product, setProduct] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    setProduct(null);
    setError(false);
    axios.get(`${API}/product/${code}`, { params: { lang } })
      .then((res) => { if (active) setProduct(res.data); })
      .catch(() => { if (active) setError(true); });
    return () => { active = false; };
  }, [code, lang]);

  if (error) {
    return (
      <div data-testid="product-not-found" className="min-h-[70vh] bg-ink text-white flex flex-col items-center justify-center gap-6 px-6">
        <p className="font-serif-display text-3xl">{L.notFound}</p>
        <Link to="/" className="inline-flex items-center gap-2 text-brass hover:text-white transition-colors">
          <ArrowLeft className="w-4 h-4" /> {L.back}
        </Link>
      </div>
    );
  }

  if (!product) {
    return (
      <div data-testid="product-loading" className="min-h-[70vh] bg-ink text-white/70 flex items-center justify-center">
        {L.loading}
      </div>
    );
  }

  return (
    <div data-testid="product-page" className="bg-ink text-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 md:py-20 grid gap-12 lg:grid-cols-2 items-center">
        {/* Product image */}
        <div className="order-1">
          <div className="relative aspect-square rounded-sm bg-[radial-gradient(circle_at_50%_40%,#f3efe7_0%,#e7ddcc_100%)] overflow-hidden flex items-center justify-center p-8">
            <img
              src={product.image}
              alt={product.name}
              data-testid="product-image"
              className="max-h-full max-w-full object-contain drop-shadow-[0_30px_50px_rgba(0,0,0,0.35)]"
            />
          </div>
        </div>

        {/* Details */}
        <div className="order-2" data-testid="product-details">
          <span className="text-brass text-xs uppercase tracking-[0.25em]">{product.category}</span>
          <h1 className="mt-3 font-serif-display font-light text-4xl md:text-6xl tracking-tight" data-testid="product-name">{product.name}</h1>
          <p className="mt-3 text-white/50 text-sm" data-testid="product-code">{L.code}: {product.code}</p>
          <div className="brass-line w-32 mt-6" />

          <p className="mt-6 text-white/60 text-sm">{L.upholstery}: <span className="text-white/85">{product.fabric}</span></p>

          <div className="mt-8">
            <p className="text-brass text-xs uppercase tracking-[0.25em] mb-4">{L.dimensions}</p>
            <div className="grid grid-cols-3 gap-3" data-testid="product-dimensions">
              {[{ i: ChevronsLeftRight, l: L.width, v: product.w }, { i: MoveHorizontal, l: L.depth, v: product.d }, { i: MoveVertical, l: L.height, v: product.h }].map((d, k) => (
                <div key={k} className="border border-white/10 px-4 py-5 text-center">
                  <d.i className="w-5 h-5 text-brass mx-auto" />
                  <p className="mt-3 text-2xl font-serif-display">{d.v}<span className="text-sm text-white/40"> cm</span></p>
                  <p className="mt-1 text-[0.7rem] uppercase tracking-wider text-white/40">{d.l}</p>
                </div>
              ))}
            </div>
          </div>

          <p className="mt-8 text-white/50 text-sm leading-relaxed">{L.custom}</p>

          <div className="mt-9 flex flex-wrap gap-4">
            <Link to="/contatti" data-testid="product-request-btn" className="inline-flex items-center gap-2 bg-brass text-white px-7 py-4 text-sm font-medium hover:bg-brass-dark transition-colors">
              <Mail className="w-4 h-4" /> {L.request}
            </Link>
            <Link to="/" data-testid="product-back-link" className="inline-flex items-center gap-2 border border-white/20 px-7 py-4 text-sm uppercase tracking-wide hover:bg-white hover:text-ink transition-colors">
              <ArrowLeft className="w-4 h-4" /> {L.back}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
